import React from 'react';
import { Text } from 'react-native';
import { OperationSearchItem, ProductionOrderSearchItem } from '@interfaces/search';
import { font as initialFont } from '@styles/fonts';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { WithColor } from '@styles/colors';
import { getJobOrOrderStatusConfig, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { ItemName, Period } from '@v2/pages/batch/details/modules/batch/components/BatchDetailsBlock/BatchDetailsBlockContent';
import StatusBadge from '@components/common/StatusBadge';
import { renderJobListItem } from '@v2/pages/productionOrder/modules/productionOrder/components/ProductionOrderJobsList';
import { ReducerEntityWithLoadingState } from '@v2/pages/common/interfaces';

type BaseParams = WithColor & {
  productionOrder: ProductionOrderSearchItem;
  scale: ScaleFunction;
  font: typeof initialFont;
};

export const getTopBlockConfig = ({ productionOrder, C, scale, font, loading }: BaseParams & { loading: boolean }) => {
  const statusConfig = getJobOrOrderStatusConfig(productionOrder?.status, C);

  return [
    {
      testID: TEST_IDS.productionOrderContent.field('quantityPlanned'),
      borderBottomRadius: false,
      borderTopRadius: false,
      onlyLabel: false,
      data: [],
      index: 0,
      i: {
        name: 'Quantity Planned',
        isText: true,
        value: productionOrder?.quantityPlanned,
      },
      loading,
    },
    // {
    //   borderBottomRadius: false,
    //   borderTopRadius: false,
    //   onlyLabel: false,
    //   data: [],
    //   index: 0,
    //   i: {
    //     name: 'Completed',
    //     isText: true,
    //     value: `${Math.round(productionOrder?.percentageComplete * 100)}%`,
    //   },
    //   loading,
    // },
    {
      testID: TEST_IDS.productionOrderContent.field('status'),
      borderBottomRadius: true,
      borderTopRadius: false,
      onlyLabel: false,
      data: [{}],
      index: 0,
      i: {
        name: 'Status',
        isText: false,
        ValueComponent: <StatusBadge C={C} config={getJobOrOrderStatusConfig(productionOrder?.status, C)} scale={scale} font={font} />,
      },
      loading: !statusConfig,
    },
  ];
};

export const getScheduleBlockConfig = ({ productionOrder, C, font }: Omit<BaseParams, 'scale'>) => [
  ...(productionOrder?.actualStartDate
    ? [
        {
          testID: TEST_IDS.productionOrderContent.field('actualStart'),
          borderBottomRadius: false,
          borderTopRadius: true,
          onlyLabel: false,
          data: [],
          index: 0,
          i: {
            name: 'Actual Start',
            ValueComponent: <Period startedOn={productionOrder?.actualStartDate} C={C} font={font} />,
          },
        },
      ]
    : []),
  ...(productionOrder?.actualEndDate
    ? [
        {
          testID: TEST_IDS.productionOrderContent.field('actualEnd'),
          borderBottomRadius: false,
          borderTopRadius: !productionOrder?.actualStartDate,
          onlyLabel: false,
          data: [],
          index: 0,
          i: {
            name: 'Actual End',
            ValueComponent: <Period startedOn={productionOrder?.actualEndDate} C={C} font={font} />,
          },
        },
      ]
    : []),
  {
    testID: TEST_IDS.productionOrderContent.field('scheduledStart'),
    borderBottomRadius: false,
    borderTopRadius: !productionOrder?.actualStartDate && !productionOrder?.actualEndDate,
    onlyLabel: false,
    data: [],
    index: 0,
    i: {
      name: 'Scheduled Start',
      ValueComponent: <Period startedOn={productionOrder?.scheduledStartDate} C={C} font={font} />,
    },
  },
  {
    testID: TEST_IDS.productionOrderContent.field('scheduledEnd'),
    borderBottomRadius: true,
    borderTopRadius: false,
    onlyLabel: false,
    data: [{}],
    index: 0,
    i: {
      name: 'Scheduled End',
      ValueComponent: <Period startedOn={productionOrder?.scheduledEndDate} C={C} font={font} />,
    },
  },
];

export const getInfoBlockConfig = ({
  productionOrder,
  C,
  scale,
  font,
  onPartNumberClick,
}: BaseParams & { onPartNumberClick: () => void }) => [
  {
    testID: TEST_IDS.productionOrderContent.field('description'),
    borderBottomRadius: false,
    borderTopRadius: true,
    onlyLabel: false,
    data: [],
    index: 0,
    i: {
      name: 'Description',
      isText: true,
      value: productionOrder?.description,
    },
  },
  ...(productionOrder?.productionOrderRowMemo
    ? [
        {
          testID: TEST_IDS.productionOrderContent.field('memo'),
          borderBottomRadius: false,
          borderTopRadius: false,
          onlyLabel: false,
          data: [],
          index: 0,
          i: {
            name: 'Memo',
            isText: true,
            value: productionOrder?.productionOrderRowMemo,
          },
        },
      ]
    : []),
  {
    testID: TEST_IDS.productionOrderContent.field('part'),
    borderBottomRadius: false,
    borderTopRadius: false,
    onlyLabel: false,
    data: [],
    index: 0,
    i: {
      name: 'Part',
      isText: false,
      ValueComponent: <ItemName {...{ C, onPress: onPartNumberClick, text: productionOrder?.partNumber, font, scale }} />,
    },
  },
  {
    testID: TEST_IDS.productionOrderContent.field('drawing'),
    borderBottomRadius: false,
    borderTopRadius: false,
    onlyLabel: false,
    data: [],
    index: 0,
    i: {
      name: 'Drawing',
      isText: true,
      value: `${productionOrder?.partDrawingNumber}${productionOrder?.partDrawingPosition ? '-' + productionOrder?.partDrawingPosition : ''}${
        productionOrder?.partDrawingRevision ? '-' + productionOrder?.partDrawingRevision : ''
      }`,
    },
  },
  {
    testID: TEST_IDS.productionOrderContent.field('customerName'),
    borderBottomRadius: true,
    borderTopRadius: false,
    onlyLabel: false,
    data: [{}],
    index: 0,
    i: {
      name: 'Customer Name',
      isText: true,
      value: productionOrder?.customerName,
    },
  },
];

export const getJobsConfig = ({
  jobs,
  C,
  scale,
  font,
  bg,
  loading,
  onJobLineClick,
}: WithColor & {
  jobs: ReducerEntityWithLoadingState<OperationSearchItem[]>;
  scale: ScaleFunction;
  font: typeof initialFont;
  bg: string;
  loading: boolean;
  onJobLineClick: (productionOperationId: number) => void;
}) =>
  Array.from(jobs.data, (j, index) => {
    const scheduledTime = j.totalTimeScheduledSeconds ?? 0;

    return {
      key: j.productionOperationId,
      borderBottomRadius: index === jobs.data?.length - 1,
      borderTopRadius: index === 0,
      onlyLabel: false,
      data: jobs.data,
      index,
      i: {
        Component: () =>
          renderJobListItem({
            ignoreDefaultStyles: true,
            CountComponent: <Text style={[[font.Body], { color: C('Label/Light/Primary') }]}>{index + 1}.</Text>,
            style: { backgroundColor: bg }, //TODO: make it pressHoverColor
            title: j.operationName || '',
            duration: scheduledTime,
            isSelected: false,
            onPress: () => onJobLineClick(j.productionOperationId),
            scale,
            C,
            font,
            bg,
          }),
        action: () => onJobLineClick(j.productionOperationId),
      },
      bg,
      C,
      loading,
    };
  });

export const getErpInfoBlockConfig = ({ productionOrder, C, font }: Omit<BaseParams, 'scale'>) => [
  {
    testID: TEST_IDS.productionOrderContent.field('modifiedOn'),
    borderBottomRadius: false,
    borderTopRadius: true,
    onlyLabel: false,
    data: [],
    index: 0,
    i: {
      name: 'Modified On',
      ValueComponent: <Period C={C} startedOn={productionOrder?.modifiedOn} font={font} />,
    },
  },
  {
    testID: TEST_IDS.productionOrderContent.field('modifiedBy'),
    borderBottomRadius: false,
    borderTopRadius: false,
    onlyLabel: false,
    data: [],
    index: 0,
    i: {
      name: 'Modified By',
      value: productionOrder?.modifiedBy,
    },
  },
  {
    testID: TEST_IDS.productionOrderContent.field('createdOn'),
    borderBottomRadius: false,
    borderTopRadius: false,
    onlyLabel: false,
    data: [],
    index: 0,
    i: {
      name: 'Created On',
      ValueComponent: <Period C={C} startedOn={productionOrder?.createdOn} font={font} />,
    },
  },
  {
    testID: TEST_IDS.productionOrderContent.field('createdBy'),
    borderBottomRadius: true,
    borderTopRadius: false,
    onlyLabel: false,
    data: [{}],
    index: 0,
    i: {
      name: 'Created By',
      value: productionOrder?.createdBy,
    },
  },
];
